import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    Input,
    OnDestroy,
    OnInit,
    Optional,
    ViewChild,
} from '@angular/core';
import { ControlContainer, FormControl, FormGroup, FormGroupDirective } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { AuthService } from '@klickdata/core/auth';
import { Group, GroupService } from '@klickdata/core/group';
import { User, UserService } from '@klickdata/core/user';
import { NotePrivacy } from '@klickdata/core/user-notes';
import { Utils } from '@klickdata/core/util';
import { Observable, of, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-shared-privacy-options',
    templateUrl: './shared-privacy-options.component.html',
    styleUrls: ['./shared-privacy-options.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
})
export class SharedPrivacyOptionsComponent implements AfterViewInit, OnDestroy, OnInit {
    @Input() public privacyControlName: string = 'privacy';
    @Input() public userIdsControlName: string = 'user_ids';
    @Input() public groupIdsControlName: string = 'group_ids';
    @ViewChild('userRecieverInput') userRecieverInput: ElementRef<HTMLInputElement>;
    @ViewChild('groupRecieverInput') groupRecieverInput: ElementRef<HTMLInputElement>;
    public privacyOptions: NotePrivacy[];
    public parentForm: FormGroup;
    public selectedRecepientFilter = new FormControl('users');
    public userCtrl = new FormControl();
    public groupCtrl = new FormControl();
    public userSearch$: Observable<User[]>;
    public groupSearch$: Observable<Group[]>;
    public userRecipients: User[] = [];
    public groupRecipients: Group[] = [];
    public authUser: User;

    private destroy: Subject<boolean> = new Subject<boolean>();

    constructor(
        @Optional() protected parentFormDirective: FormGroupDirective,
        protected userService: UserService,
        protected groupService: GroupService,
        protected changeRef: ChangeDetectorRef,
        protected authService: AuthService
    ) {
        this.parentForm = this.parentFormDirective?.form;
        this.privacyOptions = Utils.getNotesPrivacyOptions();
        this.authService
            .getUser()
            .pipe(take(1), takeUntil(this.destroy))
            .subscribe((user) => {
                this.authUser = user;
                if (!user.isAdmin()) {
                    this.privacyOptions = [this.privacyOptions[0], this.privacyOptions[3]];
                }
                this.changeRef.markForCheck();
            });
    }

    ngOnInit(): void {
        if (this.authService.getNK3PlatformValue() === 'master') {
            this.privacyOptions.push({ value: 'master', label: $localize`Master` });
        }
        // Initialize form controls if not already set
        if (this.parentForm) {
            if (!this.parentForm.get(this.userIdsControlName)) {
                this.parentForm.addControl(this.userIdsControlName, new FormControl([]));
            }
            if (!this.parentForm.get(this.groupIdsControlName)) {
                this.parentForm.addControl(this.groupIdsControlName, new FormControl([]));
            }
            if (!this.parentForm.get(this.privacyControlName)) {
                this.parentForm.addControl(this.privacyControlName, new FormControl('private'));
            }
        }

        // Initialize recipients from inputs if provided
        if (
            this.parentForm.get(this.userIdsControlName)?.value &&
            this.parentForm.get(this.userIdsControlName)?.value.length > 0
        ) {
            this.userService
                .getUsers(this.parentForm.get(this.userIdsControlName)?.value)
                .pipe(take(1), takeUntil(this.destroy))
                .subscribe((users) => {
                    this.userRecipients = [...users];
                    this.updateReciepentIds();
                    this.changeRef.markForCheck();
                });
        }
        if (
            this.parentForm.get(this.groupIdsControlName)?.value &&
            this.parentForm.get(this.groupIdsControlName)?.value.length > 0
        ) {
            this.groupService
                .getGroupsByIds(this.parentForm.get(this.groupIdsControlName)?.value)
                .pipe(take(1), takeUntil(this.destroy))
                .subscribe((groups) => {
                    this.groupRecipients = [...groups];
                    this.updateReciepentIds();
                    this.changeRef.markForCheck();
                });
        }
    }

    ngAfterViewInit(): void {
        if (this.authUser) {
            this.userSearch$ = this.userCtrl.valueChanges
                .pipe(
                    filter((term: string) => typeof term === 'string' && term !== ''),
                    debounceTime(300),
                    distinctUntilChanged()
                )
                .pipe(
                    switchMap((term) =>
                        term.trim()
                            ? this.userService.getCustomerUsers({ customerId: this.authUser.customer_id, term: term })
                            : of(<User[]>[])
                    ),
                    map((users) =>
                        users.filter(
                            (user) =>
                                this.userRecipients.findIndex((recipient) => recipient.id === user.id) === -1 &&
                                user.id !== this.authUser.id
                        )
                    ),
                    takeUntil(this.destroy)
                );

            this.groupSearch$ = this.groupCtrl.valueChanges.pipe(
                filter((term) => typeof term === 'string'),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap((term) => (term.trim() ? this.groupService.getCustomerGroups(term) : of(<Group[]>[]))),
                map((groups) =>
                    groups.filter(
                        (group) => this.groupRecipients.findIndex((recipient) => recipient.id === group.id) === -1
                    )
                ),
                takeUntil(this.destroy)
            );
        }
    }

    public selected(auto: MatAutocomplete, event: MatAutocompleteSelectedEvent, isUserSearch: boolean): void {
        if (isUserSearch) {
            this.userRecipients.push(event.option.value);
            if (this.userRecieverInput) {
                this.userRecieverInput.nativeElement.value = '';
            }
            this.userCtrl.setValue('');
        } else {
            this.groupRecipients.push(event.option.value);
            if (this.groupRecieverInput) {
                this.groupRecieverInput.nativeElement.value = '';
            }
            this.groupCtrl.setValue('');
        }

        auto.options.reset([]);
        this.updateReciepentIds();
        this.changeRef.markForCheck();
    }

    private updateReciepentIds(): void {
        if (this.parentForm) {
            if (this.parentForm.get(this.userIdsControlName)) {
                this.parentForm.get(this.userIdsControlName).setValue(this.userRecipients.map((user) => user.id));
            }
            if (this.parentForm.get(this.groupIdsControlName)) {
                this.parentForm.get(this.groupIdsControlName).setValue(this.groupRecipients.map((group) => group.id));
            }
        }
    }

    public removeUser(user: User): void {
        const index = this.userRecipients.indexOf(user);
        if (index >= 0) {
            this.userRecipients.splice(index, 1);
            this.updateReciepentIds();
            this.changeRef.markForCheck();
        }
    }

    public removeGroup(group: Group): void {
        const index = this.groupRecipients.indexOf(group);
        if (index >= 0) {
            this.groupRecipients.splice(index, 1);
            this.updateReciepentIds();
            this.changeRef.markForCheck();
        }
    }

    ngOnDestroy(): void {
        this.destroy.next(true);
        this.destroy.unsubscribe();
    }
}
